import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress,
  InputAdornment, IconButton, InputLabel, FormControl, OutlinedInput,
} from '@mui/material';
import {
  Visibility, VisibilityOff,
  ShieldOutlined, InsightsOutlined, Inventory2Outlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { color, radius, shadow } from '../theme/tokens';

const HIGHLIGHTS = [
  { icon: <Inventory2Outlined />, title: 'Catalog & inventory', text: 'Products, stock levels and low-stock alerts in one place.' },
  { icon: <InsightsOutlined />,   title: 'Live analytics',       text: 'Revenue, top brands and category performance as it happens.' },
  { icon: <ShieldOutlined />,     title: 'Secure access',        text: 'Role-based administration for your entire storefront.' },
];

const BrandMark = ({ size = 36 }) => (
  <Box
    component="img"
    src="/Hotspot Logo smart (1) - Copy.png"
    alt="HotSpot"
    sx={{
      width: 'auto',
      height: size,
      flexShrink: 0,
      objectFit: 'contain',
    }}
  />
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/api/admin/login', form);
      login(res.data.token, res.data.admin);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: color.bg }}>
      {/* Brand panel — desktop only */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '46%',
          maxWidth: 640,
          p: 6,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: color.ink[950],
          color: '#fff',
        }}
      >
        {/* Subtle solid ring accents (no gradients) */}
        <Box
          sx={{
            position: 'absolute', top: -170, right: -170,
            width: 440, height: 440, borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        />
        <Box
          sx={{
            position: 'absolute', top: -70, right: -70,
            width: 240, height: 240, borderRadius: '50%',
            border: `1px solid rgba(227,20,30,0.3)`,
          }}
        />
        <Box
          sx={{
            position: 'absolute', bottom: -190, left: -130,
            width: 440, height: 440, borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.05)',
          }}
        />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BrandMark />
          <Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}>HotSpot</Typography>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              Admin Console
            </Typography>
          </Box>
        </Box>

        <Box sx={{ position: 'relative', maxWidth: 460 }}>
          <Typography sx={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.02em' }}>
            Run your storefront with clarity.
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
            One console for orders, catalog, customers and performance — built for the HotSpot team.
          </Typography>

          <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {HIGHLIGHTS.map((h) => (
              <Box key={h.title} sx={{ display: 'flex', gap: 2 }}>
                <Box
                  sx={{
                    width: 38, height: 38, flexShrink: 0,
                    borderRadius: `${radius.lg}px`,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: color.yellow[400],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '& svg': { fontSize: 19 },
                  }}
                >
                  {h.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{h.title}</Typography>
                  <Typography sx={{ mt: 0.25, fontSize: '0.8125rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                    {h.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ position: 'relative', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
          © {new Date().getFullYear()} HotSpot Store. All rights reserved.
        </Typography>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2.5, sm: 4 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Compact brand — mobile/tablet */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <BrandMark size={34} />
            <Box>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: color.ink[900], lineHeight: 1.2 }}>
                HotSpot Admin
              </Typography>
              <Typography sx={{ fontSize: '0.6875rem', color: color.ink[500] }}>Control Panel</Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: color.ink[900], letterSpacing: '-0.015em' }}>
            Sign in
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: '0.875rem', color: color.ink[500], mb: 3.5 }}>
            Enter your administrator credentials to continue.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: color.surface,
              border: `1px solid ${color.border}`,
              borderRadius: `${radius['2xl']}px`,
              boxShadow: shadow.sm,
              p: { xs: 3, sm: 3.5 },
            }}
          >
            <TextField
              fullWidth
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              autoComplete="username"
              autoFocus
              sx={{ mb: 2.5 }}
            />

            <FormControl fullWidth variant="outlined" required>
              <InputLabel htmlFor="admin-password">Password</InputLabel>
              <OutlinedInput
                id="admin-password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="current-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3.5 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
            </Button>
          </Box>

          <Typography sx={{ mt: 2.5, textAlign: 'center', fontSize: '0.75rem', color: color.ink[400] }}>
            Protected area — authorised personnel only.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
